import React, { useEffect, useState, useMemo, useContext } from 'react';
import { collection, getDocs, orderBy, query, limit, onSnapshot, where } from 'firebase/firestore';
import { useFirestore } from '../../helpers/firebaseContext';
import { ConfigContext, MyUserDataContext } from '../../contexts';
import { getAllowedPortfolioTypeCodes } from './trades_permissions';
import {
    MantineReactTable,
    useMantineReactTable,
    type MRT_ColumnDef,
} from 'mantine-react-table';
import {
    Box,
    Group,
    ActionIcon,
    Text,
    Loader,
    Center,
    Alert,
    Stack,
    Badge,
    Tooltip,
} from '@mantine/core';
import { DatePickerInput } from '@mantine/dates';
import { fromMantineDate } from '../../helpers/mantineDates';
import { IconEdit, IconPlus, IconAlertCircle, IconArrowsLeftRight } from '@tabler/icons-react';
import { format, parse } from 'date-fns';
import { CustomClaims, ManualTrade } from '../../types';
import ManualTradeModal from './manual_trade_modal';

// --- INTERFACES ---
// interface MoneyMarketInstrument {
//     id: number;
//     issuer: string;
//     issuerCode: string;
//     days: number;
//     maturityDate: Date | null;
//     exchange: 'OTC';
//     description: string;
// }




// interface TradeFormData {
//     type: 'sukuk' | 'moneyMarket';
//     portfolioId: string;
//     transactionType: 'BUY' | 'SELL';
//     tradeDate?: Date | null;
//     settlementDate?: Date | null;
//     sukuk?: Bond;
//     moneyMarket?: MoneyMarketInstrument;
//     selectedSukukId?: string;
//     nominalValue: number;
//     pricePerUnit: number;
//     custodianAccountId?: string;
//     totalPrice: number;
//     kfhcFees: number;
//     principal: number;
//     accruedProfit: number;
//     settlementAmount: number;
//     yield: number;
//     counterpartyName?: string;
//     counterpartyAccount?: string;
//     remarks?: string;
//     status?: 'pending' | 'locked';
// }

interface Portfolio {
    id: string;
    name_en: string;
    portfolioTypeCode?: string;
    // Add other portfolio properties as needed
}

interface ManualTradesTableProps {
    date?: Date;
    toggleView?: () => void; // Optional prop to toggle view
}

// Pre-created memoized badges for BUY and SELL
const BUY_BADGE = React.memo(() => <Badge color="green">BUY</Badge>);
const SELL_BADGE = React.memo(() => <Badge color="red">SELL</Badge>);
const SUKUK_BADGE = React.memo(() => <Badge color="blue">SUKUK</Badge>);
const MM_BADGE = React.memo(() => <Badge color='grape'>MM</Badge>);

const ManualTradesTable: React.FC<ManualTradesTableProps> = ({ date, toggleView }) => {
    const db = useFirestore();
    const userContext = useContext(MyUserDataContext);
    const configContext = useContext(ConfigContext);
    const userClaims = userContext.firebaseTokenResult?.claims.customClaims as CustomClaims || {};
    const canSeeAllTrades = !!userClaims.admin || !!userClaims.oper;
    const allowedPortfolioTypeCodes = useMemo(
        () => getAllowedPortfolioTypeCodes(userContext, configContext),
        [userContext, configContext]
    );
    const [trades, setTrades] = useState<ManualTrade[]>([]);
    const [portfolios, setPortfolios] = useState<Record<string, Portfolio>>({});
    const [selectedDate, setSelectedDate] = useState<Date | null>(date || null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [addModalOpened, setAddModalOpened] = useState(false);
    const [selectedTrade, setSelectedTrade] = useState<ManualTrade | null>(null);


    // Format date to DD-MM-YYYY for Firebase path
    const formatDateForPath = (date: Date): string => {
        return format(date, 'dd-MM-yyyy');
    };

    // Parse date from DD-MM-YYYY format
    const parseDateFromPath = (dateString: string): Date => {
        return parse(dateString, 'dd-MM-yyyy', new Date());
    };

    // Get the latest trade date from Firebase
    const getLatestTradeDate = async (): Promise<string | null> => {
        try {
            const tradeDatesRef = collection(db, 'trade_dates');
            const q = query(tradeDatesRef, orderBy('__name__', 'desc'), limit(1));
            const querySnapshot = await getDocs(q);

            if (!querySnapshot.empty) {
                return querySnapshot.docs[0].id;
            }
            return null;
        } catch (err) {
            console.error('Error fetching latest trade date:', err);
            return null;
        }
    };

    // Fetch portfolios once when component mounts or date changes
    useEffect(() => {
        const fetchPortfolios = async () => {
            if (!selectedDate) return;

            try {
                const dateString = formatDateForPath(selectedDate);

                // Check date-specific portfolios first
                let portfoliosRef = collection(db, `trade_dates/${dateString}/portfolios`);
                let portfoliosSnapshot = await getDocs(portfoliosRef);

                // Fall back to general portfolios if date-specific don't exist
                if (portfoliosSnapshot.empty) {
                    portfoliosRef = collection(db, `portfolios`);
                    portfoliosSnapshot = await getDocs(portfoliosRef);
                }

                const portfoliosData: Record<string, Portfolio> = {};
                portfoliosSnapshot.forEach((doc) => {
                    portfoliosData[doc.id] = { id: doc.id, ...doc.data() } as Portfolio;
                });
                setPortfolios(portfoliosData);
            } catch (err) {
                console.error('Error fetching portfolios:', err);
                setError('Failed to fetch portfolios. Please try again.');
            }
        };

        fetchPortfolios();
    }, [selectedDate]); // Re-fetch portfolios when date changes

    // Set up real-time listener for trades
    useEffect(() => {
        let unsubscribe: (() => void) | undefined;

        const setupTradesListener = async () => {
            setLoading(true);
            setError(null);

            let dateToFetch: string;

            // If no selectedDate, get the latest date
            if (!selectedDate) {
                const latestDate = await getLatestTradeDate();
                if (latestDate) {
                    dateToFetch = latestDate;
                    setSelectedDate(parseDateFromPath(latestDate));
                } else {
                    setSelectedDate(new Date());
                    setLoading(false);
                    setError('No trade dates found in the database.');
                    return;
                }
            } else {
                dateToFetch = formatDateForPath(selectedDate);
            }

            // Non-admin/oper users with no allowed portfolio types get nothing: don't even query
            if (!canSeeAllTrades && allowedPortfolioTypeCodes.length === 0) {
                setTrades([]);
                setLoading(false);
                return;
            }

            // Set up real-time listener for trades. Authorization is enforced at the
            // data source: non-admin/oper users only fetch trades whose portfolioTypeCode
            // they are allowed to see (also enforced by Firestore security rules).
            const tradesRef = collection(db, `trade_dates/${dateToFetch}/manual_trades`);
            const tradesQuery = canSeeAllTrades
                ? query(tradesRef)
                : query(tradesRef, where('portfolioTypeCode', 'in', allowedPortfolioTypeCodes));
            unsubscribe = onSnapshot(
                tradesQuery,
                (snapshot) => {
                    const tradesData: ManualTrade[] = [];
                    snapshot.forEach((doc) => {
                        tradesData.push({ id: doc.id, ...doc.data() as ManualTrade });
                    });
                    setTrades(tradesData); // Will be empty array if no trades
                    setLoading(false);
                },
                (error) => {
                    console.error('Error listening to trades:', error);
                    setError('Failed to fetch trades. Please try again.');
                    setLoading(false);
                }
            );
        };

        setupTradesListener();

        // Cleanup function
        return () => {
            if (unsubscribe) {
                unsubscribe();
            }
        };
    }, [selectedDate, canSeeAllTrades, allowedPortfolioTypeCodes]); // Re-setup listener when date or permissions change

    // Defense-in-depth: the query above already restricts what is fetched, this
    // only re-checks the denormalized portfolioTypeCode on each trade.
    const visibleTrades = useMemo(() => {
        if (canSeeAllTrades) return trades;
        if (allowedPortfolioTypeCodes.length === 0) return [];
        return trades.filter((trade) => {
            const portfolioTypeCode = trade.portfolioTypeCode ?? portfolios[trade.portfolioId]?.portfolioTypeCode;
            return portfolioTypeCode !== undefined && allowedPortfolioTypeCodes.includes(portfolioTypeCode);
        });
    }, [trades, portfolios, canSeeAllTrades, allowedPortfolioTypeCodes]);

    // Handle date change from date picker
    const handleDateChange = (newDate: Date | null) => {
        if (newDate) {
            setSelectedDate(newDate);
            // The useEffect hooks will automatically handle fetching the new data
            // If the date has no trades, the list will remain empty
        }
    };

    // Define table columns
    const columns = useMemo<MRT_ColumnDef<ManualTrade>[]>(
        () => [
            {
                accessorKey: 'type',
                header: 'Type',
                filterVariant: 'select',
                mantineFilterSelectProps: {
                    data: ['sukuk', 'moneyMarket'],
                },
                Cell: ({ cell }) => (
                    cell.getValue<string>() === 'sukuk' ? (
                        <SUKUK_BADGE />
                    ) : (
                        <MM_BADGE />
                    )

                ),
            },
            {
                accessorKey: 'portfolioId',
                header: 'Portfolio',
                filterVariant: 'text',
                Cell: ({ cell }) => {
                    const portfolioId = cell.getValue<string>();
                    const portfolio = portfolios[portfolioId];

                    return (
                        <Stack >
                            <Text size="sm" fw={500}>{portfolioId}</Text>
                            {portfolio?.name_en && (
                                <Text size="xs" c="dimmed">{portfolio.name_en}</Text>
                            )}
                        </Stack>
                    );
                },
            },
            {
                accessorKey: 'transactionType',
                header: 'Side',
                filterVariant: 'select',
                mantineFilterSelectProps: {
                    data: ['BUY', 'SELL'],
                },
                Cell: ({ cell }) => (
                    cell.getValue<string>() === 'BUY' ? (
                        <BUY_BADGE />
                    ) : (
                        <SELL_BADGE />
                    )
                ),
            },
            {
                accessorKey: 'tradeDate',
                header: 'Trade Date',
                filterVariant: 'date',
                Cell: ({ cell }) => {
                    const timestamp = cell.getValue<any>();
                    if (!timestamp) return '-';
                    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                    return format(date, 'dd/MM/yyyy');
                },
            },
            {
                accessorKey: 'settlementDate',
                header: 'Settlement Date',
                filterVariant: 'date',
                Cell: ({ cell }) => {
                    const timestamp = cell.getValue<any>();
                    if (!timestamp) return '-';
                    const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
                    return format(date, 'dd/MM/yyyy');
                },
            },
            {
                accessorKey: 'nominalValue',
                header: 'Nominal Value',
                filterVariant: 'range',
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return value?.toLocaleString() || '0';
                },
            },
            {
                accessorKey: 'pricePerUnit',
                header: 'Price',
                filterVariant: 'range',
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return value?.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 4 }) || '0.00';
                },
            },
            {
                accessorKey: 'totalPrice',
                header: 'Total Cost',
                filterVariant: 'range',
                Cell: ({ cell }) => {
                    const value = cell.getValue<number>();
                    return value?.toLocaleString(undefined, { minimumFractionDigits: 2 }) || '0.00';
                },
            },
            {
                id: 'desc',
                header: 'Description',
                filterVariant: 'text',
                accessorFn: (row) => {
                    if (row.type === 'sukuk' && row.sukuk?.DESCRIPTION) {
                        return row.sukuk.DESCRIPTION;
                    } else if (row.type === 'moneyMarket' && row.moneyMarket?.description) {
                        return row.moneyMarket.description;
                    }
                    return '-';
                },
            },
            {
                id: 'createdBy',
                header: 'Created By',
                filterVariant: 'text',
                accessorFn: (row) => {
                    if (row?.createdBy) {
                        return row.createdBy;
                    }
                    return '-';
                },
            },
            {
                id: 'actions',
                header: 'Actions',
                enableColumnFilter: false,
                size: 120,
                Cell: ({ row }) => (
                    <ActionIcon
                        variant="subtle"
                        onClick={() => {
                            setSelectedTrade(row.original);
                            // onClick handler to be implemented
                        }}
                        disabled={row.original.state === 'LOCKED'}
                    >
                        <IconEdit size={16} />
                    </ActionIcon>
                ),
            },
        ],
        [portfolios]
    );

    const table = useMantineReactTable({
        columns,
        data: visibleTrades,
        enableColumnActions: true,
        enableColumnFilters: true,
        enablePagination: false,
        enableSorting: true,
        enableDensityToggle: false,
        enableBottomToolbar: false,
        enableColumnResizing: true,
        enableGlobalFilter: true,
        enableColumnPinning: true,
        enableStickyHeader: true,
        enableFacetedValues: true,
        columnResizeMode: 'onChange',
        initialState: {
            showColumnFilters: false,
            columnPinning: {
                right: ['actions'],
            },
        },
        mantineTableProps: {
            striped: false,
            highlightOnHover: false,
            withColumnBorders: false,
        },
        mantineTableContainerProps: {
            style: {

                maxHeight: 'calc(100dvh -  var(--app-shell-header-height) - 7.5rem)',
                overflowY: 'auto',          // keeps the scroll inside the table
            },
        },
        renderTopToolbarCustomActions: () => (
            <Group>
                <DatePickerInput
                    value={selectedDate}
                    onChange={(value) => handleDateChange(fromMantineDate(value))}
                    placeholder="Select date"
                    style={{ minWidth: 200 }}
                />
                <Tooltip label="Add New Trade" withArrow>
                    <ActionIcon
                        variant='light'
                        size='lg'
                        color='blue'
                        onClick={() => setAddModalOpened(true)}
                    >
                        <IconPlus size={16} />
                    </ActionIcon>
                </Tooltip>
                {selectedDate && <ManualTradeModal
                    key={'add-trade-modal'}
                    opened={addModalOpened}
                    onClose={() => setAddModalOpened(false)}
                    onSubmit={(trade) => {
                        console.log("New trade added", trade);
                    }}
                    tradeDate={selectedDate}
                />}
                {(selectedTrade && selectedDate) && <ManualTradeModal
                    key={'edit-trade-modal'}
                    opened={selectedTrade !== null}
                    onClose={() => setSelectedTrade(null)}
                    onSubmit={(trade) => {
                        console.log(" trade edited", trade);
                    }}
                    tradeDate={selectedDate}
                    existingTrade={selectedTrade}
                />}
                {toggleView && (
                    <Tooltip label="Show Stocks Trades" withArrow>
                        <ActionIcon
                            size='lg'
                            variant='light'
                            onClick={toggleView}
                        >
                            <IconArrowsLeftRight size={16} />
                        </ActionIcon>
                    </Tooltip>
                )}
            </Group>
        ),
    });

    if (loading) {
        return (
            <Center h={400}>
                <Loader size="lg" />
            </Center>
        );
    }

    if (error && trades.length === 0) {
        return (
            <Box p="md">
                <Alert icon={<IconAlertCircle size={16} />} color="red" mb="md">
                    {error}
                </Alert>
            </Box>
        );
    }

    return (
        <Box>
            {error && trades.length > 0 && (
                <Alert icon={<IconAlertCircle size={16} />} color="yellow" mb="md">
                    {error}
                </Alert>
            )}
            <MantineReactTable table={table} />
        </Box>
    );
};

export default ManualTradesTable;
